import React, { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import {
  RegisterAgentUploadPhotoAPI,
  SetCroppedHeadshotAPI,
  UpdateAgentDetailsAPI,
} from "services/agent";
import {
  COMPASS_CIRCLE_DISPLAY_SIZE,
  COMPASS_CIRCLE_EXPORT_SIZE,
  getCroppedImg,
  resolveUploadedPhotoUrl,
} from "./getCroppedImg";

const defaultCrop = { x: 0, y: 0 };
const CROP_SHAPE_ROUND = "round";
const CROP_SHAPE_SQUARE = "rect";
const MIN_ZOOM = 1;
const MAX_ZOOM = 1 + (3 - 1) / 3;

const clampZoom = (value) => {
  const zoomValue = Number(value);
  if (!Number.isFinite(zoomValue)) return MIN_ZOOM;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomValue));
};

const measurePmaCircleSize = (headshotRef) => {
  const el = headshotRef && headshotRef.current;
  if (!el) return COMPASS_CIRCLE_DISPLAY_SIZE;
  const rect = el.getBoundingClientRect();
  const size = Math.round(Math.min(rect.width, rect.height));
  return size > 0 ? size : COMPASS_CIRCLE_DISPLAY_SIZE;
};

const CompassPhotoEditModal = ({
  open,
  onClose,
  photoURL,
  listgenUserID,
  agentData = {},
  savedCrop,
  headshotRef,
  onSaved,
}) => {
  const [crop, setCrop] = useState(savedCrop?.crop || defaultCrop);
  const [zoom, setZoom] = useState(clampZoom(savedCrop?.zoom));
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(
    savedCrop?.croppedAreaPixels || null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pmaCircleSize, setPmaCircleSize] = useState(
    COMPASS_CIRCLE_DISPLAY_SIZE,
  );
  const [cropShape, setCropShape] = useState(
    savedCrop?.cropShape || CROP_SHAPE_SQUARE,
  );

  useEffect(() => {
    if (!open) return;
    setCrop(savedCrop?.crop || defaultCrop);
    setZoom(clampZoom(savedCrop?.zoom));
    setCroppedAreaPixels(savedCrop?.croppedAreaPixels || null);
    setCropShape(savedCrop?.cropShape || CROP_SHAPE_SQUARE);
    setErrorMessage("");
    setIsSaving(false);
    setPmaCircleSize(measurePmaCircleSize(headshotRef));
  }, [open, savedCrop, photoURL, headshotRef]);

  const handleMediaLoaded = (mediaSize) => {
    if (savedCrop && savedCrop.croppedAreaPixels) return;
    const overflowY = mediaSize.height / 2 - pmaCircleSize / 2;
    if (overflowY > 1) {
      setCrop({ x: 0, y: overflowY });
    }
  };

  const handleCropComplete = (_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  };

  const handleSave = async () => {
    if (!photoURL || !croppedAreaPixels) {
      setErrorMessage("Move the photo until the crop shows the area you want.");
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      const blob = await getCroppedImg(
        photoURL,
        croppedAreaPixels,
        COMPASS_CIRCLE_EXPORT_SIZE,
      );
      const file = new File([blob], `cropped-headshot-${listgenUserID}.jpg`, {
        type: "image/jpeg",
      });

      const compassPhotoCrop = {
        crop,
        zoom,
        croppedAreaPixels,
        cropShape,
      };

      let uploadData;
      try {
        uploadData = await SetCroppedHeadshotAPI({
          listgenUserID,
          fileData: file,
          crop,
          zoom,
          croppedAreaPixels,
          cropShape,
        });
      } catch (uploadError) {
        uploadData = null;
      }

      let croppedHeadshotURL = resolveUploadedPhotoUrl(uploadData);
      if (!croppedHeadshotURL) {
        uploadData = await RegisterAgentUploadPhotoAPI(
          "propertyHighlight",
          listgenUserID,
          file,
          true,
        );
        croppedHeadshotURL = resolveUploadedPhotoUrl(uploadData);
        if (croppedHeadshotURL) {
          await UpdateAgentDetailsAPI(
            {
              ...agentData,
              listgenUserID,
              croppedHeadshotURL,
              compassPhotoCrop,
            },
            { source: "cropped_headshot" },
          );
        }
      }
      if (!croppedHeadshotURL) {
        throw new Error("Upload did not return a photo URL.");
      }

      if (typeof onSaved === "function") {
        onSaved({ croppedHeadshotURL, compassPhotoCrop });
      }
      onClose();
    } catch (error) {
      setErrorMessage(
        (error && error.message) ||
          (typeof error === "string" ? error : "Unable to save cropped photo."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MailProDialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      classes={{
        paper: "compass-photo-edit-dialog-paper",
      }}
    >
      <MailProDialogHeader>ADMIN: Edit photo</MailProDialogHeader>
      <MailProDialogBody>
        <div className="compass-photo-edit-modal">
          <p className="compass-photo-crop-hint">
            The photo starts at the same size as on the PMA. Choose circle or
            square, then drag to pan or zoom in to crop tighter.
          </p>
          <div
            className="compass-photo-shape-toggle"
            role="group"
            aria-label="Crop shape"
          >
            <button
              type="button"
              className={cropShape === CROP_SHAPE_ROUND ? "is-active" : ""}
              onClick={() => setCropShape(CROP_SHAPE_ROUND)}
              disabled={isSaving}
            >
              Circle
            </button>
            <button
              type="button"
              className={cropShape === CROP_SHAPE_SQUARE ? "is-active" : ""}
              onClick={() => setCropShape(CROP_SHAPE_SQUARE)}
              disabled={isSaving}
            >
              Square
            </button>
          </div>
          {photoURL ? (
            open ? (
              <div
                className={`compass-photo-cropper${
                  cropShape === CROP_SHAPE_SQUARE ? " is-square-crop" : ""
                }`}
                style={{
                  width: pmaCircleSize,
                  height: pmaCircleSize,
                }}
              >
                <Cropper
                  image={photoURL}
                  crop={crop}
                  zoom={zoom}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                  aspect={1}
                  cropShape={cropShape}
                  showGrid={false}
                  objectFit="cover"
                  cropSize={{
                    width: pmaCircleSize,
                    height: pmaCircleSize,
                  }}
                  style={{
                    cropAreaStyle: {
                      width: pmaCircleSize,
                      height: pmaCircleSize,
                    },
                  }}
                  initialCroppedAreaPixels={
                    savedCrop && savedCrop.croppedAreaPixels
                      ? savedCrop.croppedAreaPixels
                      : undefined
                  }
                  onMediaLoaded={handleMediaLoaded}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                />
              </div>
            ) : null
          ) : (
            <p>No photoURL</p>
          )}
          {photoURL && (
            <label className="compass-photo-zoom-label">
              Zoom
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.01}
                value={zoom}
                onChange={(event) => setZoom(clampZoom(event.target.value))}
              />
            </label>
          )}
          {errorMessage && (
            <p className="compass-photo-crop-error">{errorMessage}</p>
          )}
          <div className="compass-photo-crop-actions">
            <MailProButton
              label="Cancel"
              variant="outlined"
              onClick={onClose}
              disabled={isSaving}
            />
            <MailProButton
              label={isSaving ? "Saving..." : "Save crop"}
              variant="filled"
              onClick={handleSave}
              disabled={isSaving || !photoURL}
            />
          </div>
        </div>
      </MailProDialogBody>
    </MailProDialog>
  );
};

export default CompassPhotoEditModal;
